import { Card, StatusBadge } from '@/shared/ui';
import { KeyValue } from '@/shared/ui/domain';
import { formatClp, formatDateTime } from '@/shared/lib/format';
import type { FundRequest, Person } from './api';
import type { ReactNode } from 'react';

/** Nombre de un actor de la solicitud (QA-009): relación obligatoria → si falta no se oculta: estado degradado visible y anomalía en consola (telemetría del navegador). */
export function personName(p: Person | null | undefined, fallback?: string | null, ctx?: string): string {
  if (p && p.full_name) return p.full_name + (p.is_active === false ? ' (usuario desactivado)' : '');
  if (fallback) return fallback;
  console.error('sgr: relación de solicitud sin usuario', { ctx, id: p?.id ?? null, missing: p?.missing ?? !p });
  return 'Usuario no disponible';
}

const REASON_LABEL: Record<string, string> = { LIMITE_EXPOSICION: 'Supera el límite individual', RENDICION_VENCIDA: 'Rendición vencida', REGLA_CONFIGURADA: 'Regla configurada' };

/** Vista completa de una solicitud (compartida por Coordinador, Finanzas, Gerencia y beneficiario). */
export function RequestCard({ r, actions }: { r: FundRequest; actions?: ReactNode }) {
  return (
    <Card title={`Solicitud #${r.id} · ${personName(r.beneficiary, r.beneficiary_name, `solicitud ${r.id}: beneficiary`)}`} actions={<div style={{ display: 'flex', gap: 8, alignItems: 'center' }}><StatusBadge status={r.status} />{actions}</div>}>
      <p style={{ fontSize: 'var(--fs-sm)', marginTop: 0 }}>{r.purpose}{r.field_notes ? ` — ${r.field_notes}` : ''}</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 'var(--sp-4)' }}>
        <div>
          <h3 style={{ fontSize: 'var(--fs-sm)', marginBottom: 6 }}>Montos</h3>
          <KeyValue rows={[
            ['FXR solicitado (nominal)', formatClp(r.fxr_requested_clp)],
            ['Saldo anterior aplicado', `− ${formatClp(r.credit_applied_clp)}`],
            ['FXR a transferir', <b key="f">{formatClp(r.fxr_to_transfer_clp)}</b>],
            ['Viáticos a transferir', formatClp(r.per_diem_total_clp)],
            ['Transferencia total requerida', <b key="t">{formatClp(r.total_to_transfer_clp)}</b>],
          ]} />
          {(r.per_diem_lines ?? []).length > 0 && (
            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--sgr-text-muted)', marginTop: 6 }}>Viáticos: {(r.per_diem_lines ?? []).map((l) => `${l.concept.toLowerCase()} ×${l.quantity} (${l.zone}, ${formatClp(l.unit_amount_clp)})`).join(' · ')}</div>
          )}
        </div>
        <div>
          <h3 style={{ fontSize: 'var(--fs-sm)', marginBottom: 6 }}>Exposición al enviar</h3>
          {r.exposure_projected_clp === null ? <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--sgr-text-muted)' }}>Se calcula al enviar la solicitud</span> : (
            <KeyValue rows={[
              ['Exposición actual', formatClp(r.exposure_current_clp ?? 0)],
              ['FXR comprometidos pendientes', formatClp(r.exposure_committed_clp ?? 0)],
              ['Exposición proyectada', <b key="p" style={{ color: r.requires_manager_exception ? 'var(--sgr-danger)' : undefined }}>{formatClp(r.exposure_projected_clp)}</b>],
              ['Límite aplicado', `${formatClp(r.limit_applied_clp ?? 0)} (${r.limit_source === 'USUARIO' ? 'individual' : 'global'})`],
            ]} />
          )}
          {r.requires_manager_exception && <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--sgr-danger)', marginTop: 6 }}>Requiere Gerencia: {(r.exception_reasons ?? []).map((x) => REASON_LABEL[x.kind] ?? x.kind).join(', ')}{r.manager_exception ? ` · ${r.manager_exception.status}${r.manager_exception.decision_note ? ` (${r.manager_exception.decision_note})` : ''}` : ''}</div>}
        </div>
        <div>
          <h3 style={{ fontSize: 'var(--fs-sm)', marginBottom: 6 }}>Imputación prevista</h3>
          {(r.allocations ?? []).length === 0 ? <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--sgr-text-muted)' }}>Sin imputación</span> : (
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 'var(--fs-sm)' }}>{(r.allocations ?? []).map((a) => <li key={a.id}>{a.target_type === 'CATEGORIA_INTERNA' ? a.internal_name : a.sgo_label}{a.amount_clp ? ` · ${formatClp(a.amount_clp)}` : ''}</li>)}</ul>
          )}
          <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--sgr-text-muted)', marginTop: 8 }}>
            Solicitante {personName(r.requester, r.requester_name, `solicitud ${r.id}: requester`)}{r.submitted_at ? ` · enviada ${formatDateTime(r.submitted_at)}` : ''}
            {r.transfer && <><br />Transferencia {formatDateTime(r.transfer.processed_at)}{r.transfer.bank_transfer_required ? ` · ${formatClp(r.transfer.bank_total_clp)}${r.transfer.bank_reference ? ` · ref. ${r.transfer.bank_reference}` : ''}` : ' · sin transferencia bancaria (cubierta con saldo previo)'}</>}
            {r.decision_reason && <><br />Motivo: {r.decision_reason}</>}
            {r.cancel_reason && <><br />Cancelada: {r.cancel_reason}</>}
          </div>
        </div>
      </div>
    </Card>
  );
}
